import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Trophy } from 'lucide-react';
import { useCompany } from '@/lib/company-context';
import { useCompanyLeaderboard } from '@/lib/queries/corporate';
import { PeriodSelector, type Period } from '@/components/shared/period-selector';
import { EmptyState } from '@/components/shared/empty-state';
import { Skeleton } from '@/components/shared/skeleton';
import { formatNumber } from '@/lib/format';

export const Route = createFileRoute('/app/leaderboard')({
  component: LeaderboardPage,
});

function LeaderboardPage() {
  const { company } = useCompany();
  const [period, setPeriod] = useState<Period>('weekly');
  const { data, isLoading } = useCompanyLeaderboard(company.slug, period, 50);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">Leaderboard</h1>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* My Rank Card */}
      {data?.my_rank && (
        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-sm font-bold text-primary tabular-nums">
              {data.my_rank.rank || '--'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {data.my_rank.display_name}
            </p>
            <p className="text-xs text-muted-foreground">Your position</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-foreground tabular-nums">
              {formatNumber(data.my_rank.points)}
            </p>
            <p className="text-xs text-muted-foreground">points</p>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              <Skeleton className="h-5 w-8" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      ) : !data?.entries?.length ? (
        <EmptyState
          icon={Trophy}
          title="No rankings yet"
          description="Rankings will appear once members start earning points."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <div className="divide-y divide-border">
            {data.entries.map((entry) => {
              const isMyRank = data.my_rank?.user_id === entry.user_id;
              return (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-3 px-4 py-3 ${isMyRank ? 'bg-primary/5' : ''}`}
                >
                  <span className="w-8 text-center text-sm font-medium tabular-nums text-muted-foreground">
                    {entry.rank <= 3
                      ? ['\u{1F947}', '\u{1F948}', '\u{1F949}'][entry.rank - 1]
                      : `#${entry.rank}`}
                  </span>
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {entry.animated_avatar_url ? (
                      <img
                        src={entry.animated_avatar_url}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-medium text-muted-foreground">
                        {entry.display_name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {entry.display_name}
                      {isMyRank && (
                        <span className="ml-1.5 text-xs text-primary font-normal">You</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-foreground tabular-nums">
                      {formatNumber(entry.points)}
                    </p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {entry.trips_count} trips
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {data.total_members > data.entries.length && (
            <div className="px-4 py-3 border-t border-border text-center">
              <p className="text-xs text-muted-foreground">
                Showing {data.entries.length} of {data.total_members} members
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
